import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import './QRTool.css';

const BASE_URL = window.location.origin;

const API = '/api/qr';

export default function QRTool({ embedded = false }) {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [codeImages, setCodeImages] = useState({});
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [error, setError] = useState('');
  const [detailCode, setDetailCode] = useState(null);

  useEffect(() => {
    fetchCodes();
  }, []);

  async function fetchCodes() {
    try {
      const res = await fetch(API);
      if (!res.ok) return;
      const data = await res.json();
      setCodes(data);
      const images = {};
      await Promise.all(data.map(async (code) => {
        const redirectUrl = `${BASE_URL}/r/${code.shortcode}`;
        images[code.shortcode] = await QRCode.toDataURL(redirectUrl, { width: 120, margin: 1 });
      }));
      setCodeImages(images);
    } catch {}
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError('');
    if (!url.startsWith('http')) { setError('URL muss mit http:// oder https:// beginnen.'); return; }
    setLoading(true);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ destinationUrl: url, label }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setNewCode(data);
      setUrl('');
      setLabel('');
      fetchCodes();
    } catch {
      setError('Fehler beim Erstellen des QR Codes.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate(shortcode) {
    if (!editUrl.startsWith('http')) { setError('Ungültige URL.'); return; }
    try {
      const res = await fetch(API, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortcode, destinationUrl: editUrl, label: editLabel }),
      });
      if (!res.ok) throw new Error();
      setEditingId(null);
      fetchCodes();
    } catch {
      setError('Fehler beim Aktualisieren.');
    }
  }

  async function handleDelete(shortcode) {
    if (!window.confirm('QR Code löschen?')) return;
    await fetch(API, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shortcode }),
    });
    if (newCode?.shortcode === shortcode) setNewCode(null);
    fetchCodes();
  }

  function downloadQR(dataUrl, shortcode) {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qr-${shortcode}.png`;
    a.click();
  }

  function startEdit(code) {
    setEditingId(code.shortcode);
    setEditUrl(code.destination_url);
    setEditLabel(code.label || '');
    setError('');
  }

  return (
    <div className={embedded ? 'qrtool-embedded' : 'qrtool-page'}>
      {!embedded && (
        <button className="back-button" onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}>
          ← Zurück
        </button>
      )}

      {!embedded && (
        <div className="qrtool-header">
          <h1>QR Code Tool</h1>
          <p className="qrtool-subtitle">
            Dynamic QR Codes: Ziel-URL jederzeit änderbar, ohne neuen Code zu generieren.
          </p>
        </div>
      )}

      <div className="qrtool-layout">
        <div className="qrtool-card">
          <h2>Neuen QR Code erstellen</h2>
          <form onSubmit={handleCreate} className="qrtool-form">
            <label>Ziel-URL</label>
            <input
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <label>Label <span className="optional">(optional)</span></label>
            <input
              type="text"
              placeholder="z.B. Meine Webseite"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            {error && <p className="qrtool-error">{error}</p>}
            <button type="submit" className="qrtool-btn qrtool-btn--primary" disabled={loading}>
              {loading ? 'Generiere…' : 'QR Code erstellen'}
            </button>
          </form>

          {newCode && (
            <div className="qrtool-result">
              <img src={newCode.qrDataUrl} alt="QR Code" className="qrtool-qr-img" />
              <div className="qrtool-result-info">
                <p className="qrtool-result-label">{newCode.label || 'Kein Label'}</p>
                <a href={newCode.redirectUrl} target="_blank" rel="noopener noreferrer" className="qrtool-link">
                  {newCode.redirectUrl}
                </a>
                <p className="qrtool-result-dest">→ {newCode.destinationUrl}</p>
                <button
                  className="qrtool-btn qrtool-btn--secondary"
                  onClick={() => downloadQR(newCode.qrDataUrl, newCode.shortcode)}
                >
                  PNG herunterladen
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="qrtool-card qrtool-card--list">
          <h2>Gespeicherte Codes <span className="qrtool-count">{codes.length}</span></h2>
          {codes.length === 0 ? (
            <p className="qrtool-empty">Noch keine QR Codes erstellt.</p>
          ) : (
            <ul className="qrtool-list">
              {codes.map((code) => (
                <li key={code.shortcode} className="qrtool-item">
                  {editingId === code.shortcode ? (
                    <div className="qrtool-edit-form">
                      <input
                        type="url"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="Neue Ziel-URL"
                      />
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        placeholder="Label"
                      />
                      <div className="qrtool-edit-actions">
                        <button className="qrtool-btn qrtool-btn--primary" onClick={() => handleUpdate(code.shortcode)}>
                          Speichern
                        </button>
                        <button className="qrtool-btn qrtool-btn--ghost" onClick={() => setEditingId(null)}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {codeImages[code.shortcode] && (
                        <img src={codeImages[code.shortcode]} alt="QR" className="qrtool-item-qr" />
                      )}
                      <div className="qrtool-item-info">
                        <span className="qrtool-item-label">{code.label || <em>Kein Label</em>}</span>
                        <a href={code.destination_url} target="_blank" rel="noopener noreferrer" className="qrtool-item-url">
                          {code.destination_url}
                        </a>
                        <span className="qrtool-item-meta">
                          /r/{code.shortcode} · {code.scan_count} Scans
                        </span>
                      </div>
                      <div className="qrtool-item-actions">
                        <button className="qrtool-btn qrtool-btn--ghost" onClick={() => setDetailCode(code)}>
                          Details
                        </button>
                        <button className="qrtool-btn qrtool-btn--ghost" onClick={() => startEdit(code)}>
                          Bearbeiten
                        </button>
                        <button className="qrtool-btn qrtool-btn--danger" onClick={() => handleDelete(code.shortcode)}>
                          Löschen
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {detailCode && (
        <div className="qrtool-modal-overlay" onClick={() => setDetailCode(null)}>
          <div className="qrtool-modal" onClick={e => e.stopPropagation()}>
            <button className="qrtool-modal-close" onClick={() => setDetailCode(null)}>✕</button>
            <h2>{detailCode.label || 'Kein Label'}</h2>
            {codeImages[detailCode.shortcode] && (
              <img src={codeImages[detailCode.shortcode]} alt="QR Code" className="qrtool-modal-qr" />
            )}
            <div className="qrtool-modal-info">
              <div className="qrtool-modal-row">
                <span className="qrtool-modal-label">Redirect-URL</span>
                <a href={`${BASE_URL}/r/${detailCode.shortcode}`} target="_blank" rel="noopener noreferrer">
                  {BASE_URL}/r/{detailCode.shortcode}
                </a>
              </div>
              <div className="qrtool-modal-row">
                <span className="qrtool-modal-label">Ziel-URL</span>
                <a href={detailCode.destination_url} target="_blank" rel="noopener noreferrer">
                  {detailCode.destination_url}
                </a>
              </div>
              <div className="qrtool-modal-row">
                <span className="qrtool-modal-label">Scans</span>
                <span>{detailCode.scan_count}</span>
              </div>
              <div className="qrtool-modal-row">
                <span className="qrtool-modal-label">Erstellt</span>
                <span>{new Date(detailCode.created_at).toLocaleDateString('de-DE')}</span>
              </div>
            </div>
            <div className="qrtool-modal-actions">
              <button
                className="qrtool-btn qrtool-btn--primary"
                onClick={() => downloadQR(codeImages[detailCode.shortcode], detailCode.shortcode)}
              >
                PNG herunterladen
              </button>
              <button className="qrtool-btn qrtool-btn--ghost" onClick={() => { setDetailCode(null); startEdit(detailCode); }}>
                Bearbeiten
              </button>
              <button className="qrtool-btn qrtool-btn--danger" onClick={() => { handleDelete(detailCode.shortcode); setDetailCode(null); }}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
